import React, { createContext, useContext } from 'react';
import {
  Configuration,
  UserApi,
  TeamApi,
  SystemApi,
  MetricsApi,
  KnowledgeApi,
  KnowledgeGraphApi,
  KnowledgeGraphTypesApi,
  GoogleDriveOAuthApi,
  SourceDocumentsApi,
  JobsApi,
  ModelsApi,
  AgentApi,
  AuthenticationApi,
  SourceApi,
  CredentialsApi,
  InvitesApi,
  ConversationsApi,
  MessagesApi,
  SearchApi,
  DefaultApi,
  MessageNotesApi,
  TestDataApi,
  AgentWideTestingApi,
  IndividualMessageTestingApi,
  POIResultsApi,
  ToolConfigurationsApi,
  SkillsApi,
  CustomToolsApi,
  ChannelsApi,
  ChatApi,
  ScheduledTasksApi,
  LoopTasksApi,
  WorkspacesApi,
  ModelCatalogApi,
  ModelProvidersApi,
  SkillsPolicyApi,
  PersonasApi,
  NetworkPolicyApi,
  ProjectsApi,
  NotificationsApi,
  ReleaseNotesApi,
  TeamFilesApi,
  WikiApi,
  DatabasesApi,
  EmailAccountApi,
  BillingApi,
  SubscriptionsApi,
  TeamWizardApi,
  ApprovalsApi,
  ExternalWorkApi,
  TeamModelDefaultsApi,
  TeamSecretsApi,
  RolesApi,
  CardNextActionsApi,
  CardWatchesApi,
  WorkStreamsApi,
  VideoContentApi,
  SSOApi,
  // Add other API clients as needed
} from '../client';
import { installSessionRetryInterceptor } from '../session-client';

// Define the context type
interface ApiContextType {
  config: Configuration;
  userApi: UserApi;
  teamApi: TeamApi;
  agentApi: AgentApi;
  authenticationApi: AuthenticationApi;
  systemApi: SystemApi;
  metricsApi: MetricsApi;
  sourceApi: SourceApi;
  knowledgeApi: KnowledgeApi;
  knowledgeGraphApi: KnowledgeGraphApi;
  knowledgeGraphTypesApi: KnowledgeGraphTypesApi;
  credentialsApi: CredentialsApi;
  googleDriveOAuthApi: GoogleDriveOAuthApi;
  sourceDocumentsApi: SourceDocumentsApi;
  jobsApi: JobsApi;
  modelsApi: ModelsApi;
  invitesApi: InvitesApi;
  conversationsApi: ConversationsApi;
  messagesApi: MessagesApi;
  searchApi: SearchApi;
  defaultApi: DefaultApi;
  messageNotesApi: MessageNotesApi;
  testDataApi: TestDataApi;
  agentWideTestingApi: AgentWideTestingApi;
  individualMessageTestingApi: IndividualMessageTestingApi;
  poiResultsApi: POIResultsApi;
  toolConfigurationsApi: ToolConfigurationsApi;
  skillsApi: SkillsApi;
  customToolsApi: CustomToolsApi;
  channelsApi: ChannelsApi;
  chatApi: ChatApi;
  scheduledTasksApi: ScheduledTasksApi;
  loopTasksApi: LoopTasksApi;
  workspacesApi: WorkspacesApi;
  modelCatalogApi: ModelCatalogApi;
  modelProvidersApi: ModelProvidersApi;
  skillsPolicyApi: SkillsPolicyApi;
  personasApi: PersonasApi;
  networkPolicyApi: NetworkPolicyApi;
  projectsApi: ProjectsApi;
  notificationsApi: NotificationsApi;
  releaseNotesApi: ReleaseNotesApi;
  teamFilesApi: TeamFilesApi;
  wikiApi: WikiApi;
  databasesApi: DatabasesApi;
  emailAccountApi: EmailAccountApi;
  billingApi: BillingApi;
  subscriptionsApi: SubscriptionsApi;
  teamWizardApi: TeamWizardApi;
  approvalsApi: ApprovalsApi;
  externalWorkApi: ExternalWorkApi;
  teamModelDefaultsApi: TeamModelDefaultsApi;
  teamSecretsApi: TeamSecretsApi;
  rolesApi: RolesApi;
  workStreamsApi: WorkStreamsApi;
  videoContentApi: VideoContentApi;
  cardNextActionsApi: CardNextActionsApi;
  cardWatchesApi: CardWatchesApi;
  ssoApi: SSOApi;
  // Add other API clients as needed
}

// Create a shared configuration object
installSessionRetryInterceptor();
const config = new Configuration({
  basePath: '',
  baseOptions: {
    headers: {
      Accept: 'application/json',
    },
    withCredentials: true,
  },
});

// Initialize API clients with the shared configuration
const userApi = new UserApi(config);
const teamApi = new TeamApi(config);
const agentApi = new AgentApi(config);
const authenticationApi = new AuthenticationApi(config);
const systemApi = new SystemApi(config);
const metricsApi = new MetricsApi(config);
const sourceApi = new SourceApi(config);
const knowledgeApi = new KnowledgeApi(config);
const knowledgeGraphApi = new KnowledgeGraphApi(config);
const knowledgeGraphTypesApi = new KnowledgeGraphTypesApi(config);
const credentialsApi = new CredentialsApi(config);
const googleDriveOAuthApi = new GoogleDriveOAuthApi(config);
const sourceDocumentsApi = new SourceDocumentsApi(config);
const jobsApi = new JobsApi(config);
const modelsApi = new ModelsApi(config);
const invitesApi = new InvitesApi(config);
const conversationsApi = new ConversationsApi(config);
const messagesApi = new MessagesApi(config);
const searchApi = new SearchApi(config);
const defaultApi = new DefaultApi(config);
const messageNotesApi = new MessageNotesApi(config);
const testDataApi = new TestDataApi(config);
const agentWideTestingApi = new AgentWideTestingApi(config);
const individualMessageTestingApi = new IndividualMessageTestingApi(config);
const poiResultsApi = new POIResultsApi(config);
const toolConfigurationsApi = new ToolConfigurationsApi(config);
const skillsApi = new SkillsApi(config);
const customToolsApi = new CustomToolsApi(config);
const channelsApi = new ChannelsApi(config);
const chatApi = new ChatApi(config);
const scheduledTasksApi = new ScheduledTasksApi(config);
const loopTasksApi = new LoopTasksApi(config);
const workspacesApi = new WorkspacesApi(config);
const modelCatalogApi = new ModelCatalogApi(config);
const modelProvidersApi = new ModelProvidersApi(config);
const skillsPolicyApi = new SkillsPolicyApi(config);
const personasApi = new PersonasApi(config);
const networkPolicyApi = new NetworkPolicyApi(config);
const projectsApi = new ProjectsApi(config);
const notificationsApi = new NotificationsApi(config);
const releaseNotesApi = new ReleaseNotesApi(config);
const teamFilesApi = new TeamFilesApi(config);
const wikiApi = new WikiApi(config);
const databasesApi = new DatabasesApi(config);
const emailAccountApi = new EmailAccountApi(config);
const billingApi = new BillingApi(config);
const subscriptionsApi = new SubscriptionsApi(config);
const teamWizardApi = new TeamWizardApi(config);
const approvalsApi = new ApprovalsApi(config);
const externalWorkApi = new ExternalWorkApi(config);
const teamModelDefaultsApi = new TeamModelDefaultsApi(config);
const teamSecretsApi = new TeamSecretsApi(config);
const rolesApi = new RolesApi(config);
const workStreamsApi = new WorkStreamsApi(config);
const videoContentApi = new VideoContentApi(config);
const cardNextActionsApi = new CardNextActionsApi(config);
const cardWatchesApi = new CardWatchesApi(config);
const ssoApi = new SSOApi(config);

// Create the context with default values
const ApiContext = createContext<ApiContextType>({
  config,
  userApi,
  teamApi,
  agentApi,
  authenticationApi,
  systemApi,
  metricsApi,
  sourceApi,
  knowledgeApi,
  knowledgeGraphApi,
  knowledgeGraphTypesApi,
  credentialsApi,
  googleDriveOAuthApi,
  sourceDocumentsApi,
  jobsApi,
  modelsApi,
  invitesApi,
  conversationsApi,
  messagesApi,
  searchApi,
  defaultApi,
  messageNotesApi,
  testDataApi,
  agentWideTestingApi,
  individualMessageTestingApi,
  poiResultsApi,
  toolConfigurationsApi,
  skillsApi,
  customToolsApi,
  channelsApi,
  chatApi,
  scheduledTasksApi,
  loopTasksApi,
  workspacesApi,
  modelCatalogApi,
  modelProvidersApi,
  skillsPolicyApi,
  personasApi,
  networkPolicyApi,
  projectsApi,
  notificationsApi,
  releaseNotesApi,
  teamFilesApi,
  wikiApi,
  databasesApi,
  emailAccountApi,
  billingApi,
  subscriptionsApi,
  teamWizardApi,
  approvalsApi,
  externalWorkApi,
  teamModelDefaultsApi,
  teamSecretsApi,
  rolesApi,
  workStreamsApi,
  videoContentApi,
  cardNextActionsApi,
  cardWatchesApi,
  ssoApi,
  // Add other API clients as needed
});

// Create a provider component
export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // The value object contains all the API clients
  const value = {
    config,
    userApi,
    teamApi,
    agentApi,
    authenticationApi,
    systemApi,
    metricsApi,
    sourceApi,
    knowledgeApi,
    knowledgeGraphApi,
    knowledgeGraphTypesApi,
    credentialsApi,
    googleDriveOAuthApi,
    sourceDocumentsApi,
    jobsApi,
    modelsApi,
    invitesApi,
    conversationsApi,
    messagesApi,
    searchApi,
    defaultApi,
    messageNotesApi,
    testDataApi,
    agentWideTestingApi,
    individualMessageTestingApi,
    poiResultsApi,
    toolConfigurationsApi,
    skillsApi,
    customToolsApi,
    channelsApi,
    chatApi,
    scheduledTasksApi,
    loopTasksApi,
    workspacesApi,
    modelProvidersApi,
    skillsPolicyApi,
    personasApi,
    networkPolicyApi,
    projectsApi,
    notificationsApi,
    releaseNotesApi,
    teamFilesApi,
    wikiApi,
    databasesApi,
    emailAccountApi,
    billingApi,
    subscriptionsApi,
    teamWizardApi,
    approvalsApi,
    externalWorkApi,
    modelCatalogApi,
    teamModelDefaultsApi,
    teamSecretsApi,
    rolesApi,
    workStreamsApi,
    videoContentApi,
    cardNextActionsApi,
    cardWatchesApi,
    ssoApi,
    // Add other API clients as needed
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

// Create a custom hook for using the API context
export const useApi = () => useContext(ApiContext);
